import { AbstractConnector } from '@web3-react/abstract-connector'
import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core'
import { WalletConnectConnector } from '@web3-react/walletconnect-connector'
import Row, { RowBetween, RowFixed } from 'components/Row'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import ReactGA from 'react-ga'
import styled from 'styled-components/macro'
import { fortmatic, portis } from '../../connectors'
import { OVERLAY_READY } from '../../connectors/Fortmatic'
import { ADD_CHAIN_CONFIG, NETWORK_LABELS, NETWORK_NAME, SUPPORTED_WALLETS } from '../../constants/wallet'
import usePrevious from '../../hooks/usePrevious'
import { ApplicationModal, WalletModalTriggerSource } from '../../state/application/actions'
import { useModalOpen, useWalletModalToggle } from '../../state/application/hooks'
import { ExternalLink, StyledCloseIcon, TYPE } from '../../theme'
import AccountDetails from '../AccountDetails'

import Modal from '../Modal'
import Option from './Option'
import PendingView from './PendingView'
import useTheme from 'hooks/useTheme'
import { useWalletTriggerSource } from 'state/user/hooks'
import { IconWalletInfo12 } from 'xterio-icons'
import { darken } from 'polished'
import { WALLET_DOWNLOAD_URLS, WalletSDKType } from 'connectors/customConnectors'

const Wrapper = styled.div`
  ${({ theme }) => theme.flexColumnNoWrap}
  margin: 0;
  padding: 0;
  width: 100%;
`

const ContentWrapper = styled.div`
  margin-top: 16px;
  align-items: center;
  justify-items: center;
`

const UpperSection = styled.div`
  position: relative;
  padding: 24px;
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    padding: 18px 20px;
  `};

  h5 {
    margin: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: 400;
  }

  h5:last-child {
    margin-bottom: 0px;
  }

  h4 {
    margin-top: 0;
    font-weight: 500;
  }
`

const OptionGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: repeat(${({ columns }) => columns || '2'}, 1fr);
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    grid-template-columns: repeat(1, 1fr);
  `};
`
const OptionItemChain = styled.div`
  background-color: ${({ theme }) => theme.bg02};
  display: flex;
  flex-direaction: row;
  align-items: center;
  padding: 14px 16px;
  border-radius: 12px;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.bg02)};
  }
  .icon {
    width: 32px;
    height: 32px;
    margin-right: 12px;
  }
`

const HoverText = styled.div`
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.white};
  display: flex;
  align-items: center;

  :hover {
    cursor: pointer;
  }
`

const WALLET_VIEWS = {
  OPTIONS: 'options',
  OPTIONS_SECONDARY: 'options_secondary',
  ACCOUNT: 'account',
  PENDING: 'pending',
}

export default function WalletModal({
  pendingTransactions,
  confirmedTransactions,
  ENSName,
}: {
  pendingTransactions: string[] // hashes of pending
  confirmedTransactions: string[] // hashes of confirmed
  ENSName?: string
}) {
  const theme = useTheme()
  // important that these are destructed from the account-specific web3-react context
  const { active, account, connector, activate, error } = useWeb3React()

  const [walletView, setWalletView] = useState(WALLET_VIEWS.ACCOUNT)

  const [pendingWallet, setPendingWallet] = useState<AbstractConnector | undefined>()

  const [pendingError, setPendingError] = useState<boolean>()

  const walletModalOpen = useModalOpen(ApplicationModal.WALLET)
  const toggleWalletModal = useWalletModalToggle()
  const [triggerSource] = useWalletTriggerSource()

  const previousAccount = usePrevious(account)

  // close on connection, when logged out before
  useEffect(() => {
    if (account && !previousAccount && walletModalOpen) {
      toggleWalletModal()
    }
  }, [account, previousAccount, toggleWalletModal, walletModalOpen])

  // always reset to account view
  useEffect(() => {
    if (walletModalOpen) {
      setPendingError(false)
      setWalletView(WALLET_VIEWS.ACCOUNT)
    }
  }, [walletModalOpen])

  // close modal when a connection is successful
  const activePrevious = usePrevious(active)
  const connectorPrevious = usePrevious(connector)
  useEffect(() => {
    if (walletModalOpen && ((active && !activePrevious) || (connector && connector !== connectorPrevious && !error))) {
      setWalletView(WALLET_VIEWS.ACCOUNT)
    }
  }, [setWalletView, active, error, connector, walletModalOpen, activePrevious, connectorPrevious])

  const tryActivation = async (connector: AbstractConnector | undefined) => {
    let name = ''
    Object.keys(SUPPORTED_WALLETS).map((key) => {
      if (connector === SUPPORTED_WALLETS[key].connector) {
        return (name = SUPPORTED_WALLETS[key].name)
      }
      return true
    })
    // log selected wallet
    ReactGA.event({
      category: 'Wallet',
      action: 'Change Wallet',
      label: name,
    })
    setPendingWallet(connector) // set wallet for pending view
    setWalletView(WALLET_VIEWS.PENDING)

    // if the connector is walletconnect and the user has already tried to connect, manually reset the connector
    if (connector instanceof WalletConnectConnector && connector.walletConnectProvider?.wc?.uri) {
      connector.walletConnectProvider = undefined
    }

    connector &&
      (await activate(connector, undefined, true).catch((error) => {
        if (error instanceof UnsupportedChainIdError) {
          activate(connector) // a little janky...can't use setError because the connector isn't set
        } else {
          setPendingError(true)
        }
      }))
  }
  const onSwitchChain = useCallback(
    async (network?: NETWORK_NAME, isSwitch?: boolean) => {
      const config = ADD_CHAIN_CONFIG[network || NETWORK_NAME.XTERIO]
      if (!connector) {
        console.log('onSwitchChain connector is undefined')
        return
      }
      const provider = await connector?.getProvider()
      await provider
        ?.request?.({
          method: 'wallet_addEthereumChain',
          params: [config],
        })
        .then(() => {
          console.log('Xterio Chain (BNB) added successfully.')
        })
        .catch((error: any) => {
          console.error('Failed to add Xterio Chain (BNB).', error)
        })
      isSwitch && toggleWalletModal()
    },
    [connector, toggleWalletModal]
  )

  // close wallet modal if fortmatic modal is active
  useEffect(() => {
    fortmatic.on(OVERLAY_READY, () => {
      toggleWalletModal()
    })
  }, [toggleWalletModal])

  // 首先定义一个检查钱包是否已安装的函数
  const checkWalletInstalled = (key: string) => {
    switch (key) {
      case WalletSDKType.OKX:
        return !!window?.okxwallet
      case WalletSDKType.BYBIT:
        return !!window?.bybitWallet
      case WalletSDKType.GATE:
        return !!window?.gatewallet
      case WalletSDKType.METAMASK:
        return !!window?.ethereum?.isMetaMask
      default:
        return true
    }
  }

  // 修改 getOptions 函数
  function getOptions() {
    return Object.keys(SUPPORTED_WALLETS).map((key) => {
      const option = SUPPORTED_WALLETS[key]

      // 移动端处理
      if (isMobile) {
        if (option.connector === portis) return null
        if (!checkWalletInstalled(key) && option.mobile) {
          return (
            <Option
              onClick={() => {
                option.connector !== connector && !option.href && tryActivation(option.connector)
              }}
              id={`connect-${key}`}
              key={key}
              active={option.connector && option.connector === connector}
              color={option.color}
              link={option.href}
              header={option.name}
              subheader={null}
              icon={option.iconURL}
            />
          )
        }
        return null
      }

      // PC端处理
      if (!checkWalletInstalled(key)) {
        // console.log(`${option.name} wallet not installed ${key}`)
        return (
          <Option
            id={`connect-${key}`}
            key={key}
            color={option.color}
            header={`Install ${option.name}`}
            subheader={null}
            link={WALLET_DOWNLOAD_URLS[key as WalletSDKType]}
            icon={option.icon || option.iconURL}
          />
        )
      }

      // 已安装钱包的选项
      return (
        !isMobile &&
        !option.mobileOnly && (
          <Option
            id={`connect-${key}`}
            onClick={() => {
              option.connector === connector
                ? setWalletView(WALLET_VIEWS.ACCOUNT)
                : !option.href && tryActivation(option.connector)
            }}
            key={key}
            active={option.connector === connector}
            color={option.color}
            link={option.href}
            header={option.name}
            subheader={null}
            icon={option.icon || option.iconURL}
          />
        )
      )
    })
  }

  function ErrorNetWork() {
    const isConnected = !error && account && walletView === WALLET_VIEWS.ACCOUNT
    const isErrorNet = error instanceof UnsupportedChainIdError
    return (
      <UpperSection>
        <RowBetween>
          <TYPE.titleText>
            {isConnected ? 'Switch Network' : isErrorNet ? 'Wrong Network' : 'Error connecting'}
          </TYPE.titleText>
          <StyledCloseIcon onClick={toggleWalletModal} />
        </RowBetween>
        {!isConnected && (
          <ContentWrapper>
            <RowFixed width={'100% !important'}>
              <IconWalletInfo12 size={16} color={theme.red1} />
              <TYPE.mediumText fontSize={14} ml={'4px'} color={theme.red1}>
                {isErrorNet ? 'Please connect to BNB Chain.' : 'Error connecting. Try refreshing the page.'}
              </TYPE.mediumText>
            </RowFixed>
          </ContentWrapper>
        )}
        {(isConnected || isErrorNet) && (
          <OptionGrid style={{ marginTop: '16px' }} columns={1}>
            {Object.entries(NETWORK_LABELS).map(([chaindId, item]) => {
              const { icon: Icon, name, network, canSwitch } = item || {}
              return !canSwitch ? null : (
                <OptionItemChain key={chaindId} onClick={() => onSwitchChain(network, !!isConnected)}>
                  {Icon ? <Icon className="icon" size={32} /> : null}
                  <TYPE.nomalText>{name}</TYPE.nomalText>
                </OptionItemChain>
              )
            })}
          </OptionGrid>
        )}
      </UpperSection>
    )
  }
  function getModalContent() {
    if (error || (account && walletView === WALLET_VIEWS.ACCOUNT && triggerSource === WalletModalTriggerSource.chain)) {
      return <ErrorNetWork />
    }
    if (account && walletView === WALLET_VIEWS.ACCOUNT) {
      return (
        <AccountDetails
          toggleWalletModal={toggleWalletModal}
          pendingTransactions={pendingTransactions}
          confirmedTransactions={confirmedTransactions}
          ENSName={ENSName}
          openOptions={() => setWalletView(WALLET_VIEWS.OPTIONS)}
        />
      )
    }
    return (
      <UpperSection>
        <RowBetween style={{ marginBottom: 16 }}>
          {walletView !== WALLET_VIEWS.ACCOUNT ? (
            <HoverText
              onClick={() => {
                setPendingError(false)
                setWalletView(WALLET_VIEWS.ACCOUNT)
              }}
            >
              Back
            </HoverText>
          ) : (
            <TYPE.white fontSize={16}>Connect to a wallet</TYPE.white>
          )}
          <StyledCloseIcon onClick={toggleWalletModal} />
        </RowBetween>

        <Row style={{ marginBottom: 16 }}>
          <TYPE.subHeader fontSize={14} color={theme.text60}>
            By connecting a wallet, you acknowledge that you have read and understood, and have agreed to, the{' '}
            <ExternalLink href="https://www.xter.io/legal/xterswap-terms-of-service/">
              Xterswap Terms of Service
            </ExternalLink>
            .{/* <ExternalLink href="https://uniswap.org/disclaimer/">Xterswap protocol disclaimer</ExternalLink>. */}
          </TYPE.subHeader>
        </Row>

        {walletView === WALLET_VIEWS.PENDING ? (
          <PendingView
            connector={pendingWallet}
            error={pendingError}
            setPendingError={setPendingError}
            tryActivation={tryActivation}
          />
        ) : (
          <OptionGrid>{getOptions()}</OptionGrid>
        )}
      </UpperSection>
    )
  }

  const showNet = useMemo(
    () => error || (account && walletView === WALLET_VIEWS.ACCOUNT && triggerSource === WalletModalTriggerSource.chain),
    [account, error, triggerSource, walletView]
  )

  return (
    <Modal
      width={showNet ? '400px' : '600px'}
      isOpen={walletModalOpen}
      onDismiss={toggleWalletModal}
      minHeight={false}
      maxHeight={90}
    >
      <Wrapper>{getModalContent()}</Wrapper>
    </Modal>
  )
}
